import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Forsakringsbolag = {
    id: number;
    namn: string;
    ordning: number;
};

export type ForsakringsbolagDisplay = {
    value: Forsakringsbolag["id"];
    label: string;
};

type State = BaseState & {
    forsakringsbolag: Map<Forsakringsbolag["id"], Forsakringsbolag>;
};

export const convertForsakringsbolagData: (args: {
    forsakringsbolag?: Forsakringsbolag;
}) => ForsakringsbolagDisplay | null = ({ forsakringsbolag }) => {
    if (!forsakringsbolag) {
        return null;
    }

    return {
        value: forsakringsbolag.id,
        label: forsakringsbolag.namn,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        forsakringsbolag: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "forsakringsbolag";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        forsakringsbolag: (state: State) => [...state.forsakringsbolag.values()],
        forsakringsbolagSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertForsakringsbolagData({ forsakringsbolag: singleResult }));
            }

            return result;
        },
        forsakringsbolagById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.forsakringsbolag.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addForsakringsbolag: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                forsakringsbolag = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                forsakringsbolag: Forsakringsbolag[];
            }
        ) =>
            subsetSetter<State, Forsakringsbolag>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: forsakringsbolag,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchForsakringsbolag: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Forsakringsbolag>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "core/forsakringsbolag/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addForsakringsbolag", {
                    subsetName,
                    offset: resultOffset,
                    forsakringsbolag: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertForsakringsbolagData({ forsakringsbolag: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
